package co.uk.paxes.pinpoint.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/**
 * TurnBasedUIManager handles UI visibility based on player turns and types.
 * Manages the visibility of ad-related buttons during AI vs human player turns.
 */
object TurnBasedUIManager {
    
    // Turn change callback
    private var turnChangeCallback: ((Boolean) -> Unit)? = null
    
    /**
     * Data class representing the current turn state.
     */
    data class TurnState(
        val currentPlayerId: String,
        val isCurrentPlayerAI: Boolean,
        val isMyTurn: Boolean,
        val shouldShowAdButtons: Boolean
    ) {
        companion object {
            /**
             * Create TurnState from game metadata and player information.
             */
            fun create(
                currentPlayerId: String,
                localPlayerId: String,
                players: Map<String, SharedBoardManager.SharedPlayer>
            ): TurnState {
                val currentPlayer = players[currentPlayerId]
                val isCurrentPlayerAI = currentPlayer?.isAI ?: false
                val isMyTurn = currentPlayerId == localPlayerId
                val shouldShowAdButtons = isMyTurn && !isCurrentPlayerAI
                
                console.log("TurnState.create: currentPlayerId=$currentPlayerId, localPlayerId=$localPlayerId")
                console.log("TurnState.create: currentPlayer=${currentPlayer?.name}, isAI=${currentPlayer?.isAI}")
                console.log("TurnState.create: isCurrentPlayerAI=$isCurrentPlayerAI, isMyTurn=$isMyTurn, shouldShowAdButtons=$shouldShowAdButtons")
                
                return TurnState(
                    currentPlayerId = currentPlayerId,
                    isCurrentPlayerAI = isCurrentPlayerAI,
                    isMyTurn = isMyTurn,
                    shouldShowAdButtons = shouldShowAdButtons
                )
            }
        }
    }
    
    /**
     * Interface for detecting player types and turn status.
     */
    interface PlayerTypeDetector {
        fun isPlayerAI(playerId: String): Boolean
        fun isMyTurn(): Boolean
        fun getCurrentPlayer(): SharedBoardManager.SharedPlayer?
        fun getCurrentPlayerId(): String?
        fun getLocalPlayerId(): String?
        fun getAllPlayers(): Map<String, SharedBoardManager.SharedPlayer>
    }
    
    /**
     * Default implementation of PlayerTypeDetector using InternetGameManager.
     */
    object DefaultPlayerTypeDetector : PlayerTypeDetector {
        override fun isPlayerAI(playerId: String): Boolean {
            val players = InternetGameManager.getPlayers()
            return players[playerId]?.isAI ?: false
        }
        
        override fun isMyTurn(): Boolean {
            return InternetGameManager.isMyTurn()
        }
        
        override fun getCurrentPlayer(): SharedBoardManager.SharedPlayer? {
            val meta = InternetGameManager.getGameMeta()
            val players = InternetGameManager.getPlayers()
            return meta?.currentPlayerId?.let { players[it] }
        }
        
        override fun getCurrentPlayerId(): String? {
            return InternetGameManager.getGameMeta()?.currentPlayerId
        }
        
        override fun getLocalPlayerId(): String? {
            return SharedBoardManager.getCurrentPlayerId()
        }
        
        override fun getAllPlayers(): Map<String, SharedBoardManager.SharedPlayer> {
            return InternetGameManager.getPlayers()
        }
    }
    
    // Current player type detector (can be swapped for testing)
    private var playerTypeDetector: PlayerTypeDetector = DefaultPlayerTypeDetector
    
    /**
     * Set a custom player type detector (useful for testing).
     */
    fun setPlayerTypeDetector(detector: PlayerTypeDetector) {
        playerTypeDetector = detector
    }
    
    /**
     * Get the current turn state.
     */
    fun getCurrentTurnState(): TurnState? {
        val currentPlayerId = playerTypeDetector.getCurrentPlayerId()
        val localPlayerId = playerTypeDetector.getLocalPlayerId()
        val players = playerTypeDetector.getAllPlayers()
        
        console.log("TurnBasedUIManager.getCurrentTurnState: currentPlayerId=$currentPlayerId, localPlayerId=$localPlayerId")
        console.log("TurnBasedUIManager.getCurrentTurnState: players=${players.keys.toList()}")
        
        if (currentPlayerId == null || localPlayerId == null) {
            console.log("TurnBasedUIManager.getCurrentTurnState: Missing IDs, returning null")
            return null
        }
        
        val currentPlayer = players[currentPlayerId]
        console.log("TurnBasedUIManager.getCurrentTurnState: currentPlayer=${currentPlayer?.name}, isAI=${currentPlayer?.isAI}")
        
        return TurnState.create(currentPlayerId, localPlayerId, players)
    }
    
    /**
     * Check if the current player is human (not AI).
     */
    fun isCurrentPlayerHuman(): Boolean {
        val currentPlayerId = playerTypeDetector.getCurrentPlayerId() ?: return true
        return !playerTypeDetector.isPlayerAI(currentPlayerId)
    }
    
    /**
     * Update button visibility based on whether it's a human player's turn.
     * @param isHumanPlayerTurn true if it's a human player's turn, false for AI
     */
    fun updateButtonVisibility(isHumanPlayerTurn: Boolean) {
        console.log("TurnBasedUIManager: updateButtonVisibility called with isHumanPlayerTurn=$isHumanPlayerTurn")
        
        // Get all watch ad buttons by class name
        val watchAdButtons = document.getElementsByClassName("btn-watch-ad")
        
        // Handle different undo button IDs for different game modes
        val undoBtn = document.getElementById("btn-undo") as? HTMLButtonElement  // Multiplayer
        val localUndoBtn = document.getElementById("local-btn-undo") as? HTMLButtonElement  // Local game
        val aiUndoBtn = document.getElementById("ai-btn-undo") as? HTMLButtonElement  // AI game
        
        // Handle different credits display IDs
        val creditsDisplay = document.getElementById("undo-credits-display") as? HTMLElement  // Multiplayer
        val localCreditsDisplay = document.getElementById("local-undo-credits-display") as? HTMLElement  // Local game
        
        if (isHumanPlayerTurn) {
            // Show buttons for human players
            console.log("TurnBasedUIManager: Showing ad buttons for human player turn")
            for (i in 0 until watchAdButtons.length) {
                val button = watchAdButtons.item(i) as? HTMLElement
                button?.style?.display = "inline-block"
            }
            undoBtn?.style?.display = "block"
            localUndoBtn?.style?.display = "block"
            aiUndoBtn?.style?.display = "block"
            creditsDisplay?.style?.display = "inline-flex"
            localCreditsDisplay?.style?.display = "inline-flex"
        } else {
            // Hide buttons for AI players
            console.log("TurnBasedUIManager: Hiding ad buttons for AI player turn")
            for (i in 0 until watchAdButtons.length) {
                val button = watchAdButtons.item(i) as? HTMLElement
                button?.style?.display = "none"
            }
            undoBtn?.style?.display = "none"
            localUndoBtn?.style?.display = "none"
            aiUndoBtn?.style?.display = "none"
            creditsDisplay?.style?.display = "none"
            localCreditsDisplay?.style?.display = "none"
        }
    }
    
    /**
     * Subscribe to turn changes and automatically update button visibility.
     * @param callback Called with true if it's a human player's turn, false for AI
     */
    fun subscribeToTurnChanges(callback: (Boolean) -> Unit) {
        console.log("TurnBasedUIManager: Subscribing to turn changes")
        turnChangeCallback = callback
    }
    
    /**
     * Notify of a turn change. Should be called when the current player changes.
     * @param newPlayerId The ID of the new current player
     */
    fun onTurnChanged(newPlayerId: String) {
        console.log("TurnBasedUIManager: Turn changed to player: $newPlayerId")
        
        val players = playerTypeDetector.getAllPlayers()
        val localPlayerId = playerTypeDetector.getLocalPlayerId()
        val currentPlayer = players[newPlayerId]
        
        console.log("TurnBasedUIManager: localPlayerId=$localPlayerId")
        console.log("TurnBasedUIManager: newPlayer=${currentPlayer?.name}, isAI=${currentPlayer?.isAI}")
        console.log("TurnBasedUIManager: All players: ${players.entries.map { "${it.key}:${it.value.name}(AI=${it.value.isAI})" }}")
        
        val isHumanTurn = !playerTypeDetector.isPlayerAI(newPlayerId)
        val isMyTurn = newPlayerId == localPlayerId
        val shouldShowButtons = isMyTurn && isHumanTurn
        
        console.log("TurnBasedUIManager: isHumanTurn=$isHumanTurn, isMyTurn=$isMyTurn, shouldShowButtons=$shouldShowButtons")
        
        // Update button visibility
        updateButtonVisibility(shouldShowButtons)
        
        // Notify callback
        turnChangeCallback?.invoke(shouldShowButtons)
    }
    
    /**
     * Initialize the turn-based UI manager.
     * Sets up initial button visibility and subscribes to game state changes.
     */
    fun initialize() {
        console.log("TurnBasedUIManager: Initializing")
        
        // Set initial button visibility
        val turnState = getCurrentTurnState()
        if (turnState != null) {
            updateButtonVisibility(turnState.shouldShowAdButtons)
        }
        
        console.log("TurnBasedUIManager: Initialization complete")
    }
    
    /**
     * Clean up the turn-based UI manager.
     */
    fun cleanup() {
        console.log("TurnBasedUIManager: Cleaning up")
        turnChangeCallback = null
    }
}